import { init, browserTracingIntegration } from '@sentry/vue';

// @sentry/vue@8 doesn't export its Options type directly — derive it structurally
// from init()'s own parameter type instead of guessing the internal export path.
type TSentryConfig = Parameters<typeof init>[0];

const defaultEnv: string = 'development';

export interface IEnvironmentConfig {
  adminGateway: string,
  bookingApp: string,
  tourWidget: string,
  oauthService: string,
  s3Bucket: string,
  s3Url: string,
  uploader: string,
  autoenhance: string,
  dynamicApiUrl: string,
  hydraUrl: string,
  fpnLatestScriptUrl: string,
  fpnV25ScriptUrl: string,
  fpnV2ScriptUrl: string,
}
/**
 * Ensures a given url replaces its .dev domain with a .io when window origin is at .io
 * @param url
 * @returns
 */
const swapToIO = (url: string) => {
  if (window?.location?.origin?.endsWith('.io') && window?.APP_CONFIG.VUE_APP_ENV !== 'production') {
    return url.replace('.dev', '.io');
  }

  return url;
};

const config: Record<string, IEnvironmentConfig> = {
  staging: {
    adminGateway: 'https://admin-gateway.staging.rentsync.dev',
    bookingApp: 'https://booking-gateway-rb.staging.rentsync.dev',
    tourWidget: 'https://tourswidget.staging.rentsync.dev',
    oauthService: 'https://oauth.staging.rentsync.dev',
    s3Bucket: 'lws_lift_staging',
    s3Url: swapToIO('https://assets.staging.rentsync.dev'),
    uploader: 'https://uploader.staging.rentsync.dev',
    autoenhance: 'https://api.autoenhance.ai/v2',
    dynamicApiUrl: 'https://dynamic-api.staging.rentsync.dev',
    hydraUrl: 'https://billing-system-lift-staging.staging.rentsync.dev',
    fpnLatestScriptUrl: 'https://cdn.rentsync.com/site/fpn/latest-cms/floorplan-navigator.min.js',
    fpnV25ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-5-cms/floorplan-navigator.min.js',
    fpnV2ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-cms/floorplan-navigator.min.js',
  },

  development: {
    adminGateway: 'https://admin-gateway.development.rentsync.dev',
    bookingApp: 'https://booking-gateway-rb.development.rentsync.dev',
    tourWidget: 'https://tourswidget.development.rentsync.dev',
    oauthService: 'https://oauth.development.rentsync.dev',
    s3Bucket: 'lws_lift_development',
    s3Url: swapToIO('https://assets.development.rentsync.dev'),
    uploader: 'https://uploader.development.rentsync.dev',
    autoenhance: 'https://api.autoenhance.ai/v2',
    dynamicApiUrl: 'https://dynamic-api.development.rentsync.dev',
    hydraUrl: 'https://billing-system.development.rentsync.dev',
    fpnLatestScriptUrl: 'https://cdn.rentsync.com/site/fpn/latest-cms/floorplan-navigator.min.js',
    fpnV25ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-5-cms/floorplan-navigator.min.js',
    fpnV2ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-cms/floorplan-navigator.min.js',
  },

  demo: {
    adminGateway: 'https://admin-gateway.demo.rentsync.dev',
    bookingApp: 'https://booking-gateway-rb.demo.rentsync.dev',
    tourWidget: 'https://tourswidget.demo.rentsync.dev',
    oauthService: 'https://oauth.demo.rentsync.dev',
    s3Bucket: 'lws_lift_demo',
    s3Url: swapToIO('https://assets.demo.rentsync.dev'),
    uploader: 'https://uploader.demo.rentsync.dev',
    autoenhance: 'https://api.autoenhance.ai/v2',
    dynamicApiUrl: 'https://dynamic-api.demo.rentsync.dev',
    hydraUrl: 'https://example.com',
    fpnLatestScriptUrl: 'https://cdn.rentsync.com/site/fpn/latest-cms/floorplan-navigator.min.js',
    fpnV25ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-5-cms/floorplan-navigator.min.js',
    fpnV2ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-cms/floorplan-navigator.min.js',
  },

  production: {
    adminGateway: 'https://admin-gateway.rentsync.com',
    bookingApp: 'https://booking.rentsync.com',
    tourWidget: 'https://tourswidget.rentsync.com',
    oauthService: 'https://oauth.rentsync.com',
    s3Bucket: 'lws_lift',
    s3Url: 'https://assets.rentsync.com',
    uploader: 'https://uploader.rentsync.com',
    autoenhance: 'https://api.autoenhance.ai/v2',
    dynamicApiUrl: 'https://dynamic-api.rentsync.com',
    hydraUrl: 'https://hydra-billing.rentsync.com',
    fpnLatestScriptUrl: 'https://cdn.rentsync.com/site/fpn/latest-cms/floorplan-navigator.min.js',
    fpnV25ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-5-cms/floorplan-navigator.min.js',
    fpnV2ScriptUrl: 'https://cdn.rentsync.com/site/fpn/v2-cms/floorplan-navigator.min.js',
  },
};

const localhostEnv = (port: string, baseEnv: keyof typeof config = 'development') => ({
  ...config[baseEnv],
  adminGateway: `http://localhost:${port}`,
});

const appEnv = window.APP_CONFIG?.VUE_APP_ENV || defaultEnv;
const localGateway = window?.APP_CONFIG?.VUE_APP_LOCAL_GATEWAY;

export const maxUploadSize = 32000000;

const appEnvConfig = (
  localGateway?.startsWith('localhost')
    ? localhostEnv(localGateway.split(':')[1], appEnv)
    : config[appEnv]
);

export default appEnvConfig;

export const sentryConfig: TSentryConfig = {
  dsn: 'https://81779fc0f42df53085fab7d70caab323@o851802.ingest.us.sentry.io/5818634',
  integrations: [browserTracingIntegration()],
  sampleRate: 0.5,
  environment: window?.APP_CONFIG?.VUE_APP_ENV,
  release: window?.APP_CONFIG?.VUE_APP_VERSION,
  denyUrls: [
    /booking-rb\.staging\.rentsync\.dev/i,
    /sisense\.rentsync\.com/i,
    /booking-rb\.rentsync\.com/i,
    /booking\.rentsync\.com/i,
  ],
  ignoreErrors: [
    'Request failed with status code 401',
    'Navigation cancelled',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed',
    'Sisense IFrame',
    'Navigation Duplicated',
    `Cannot read properties of null (reading 'unselectable')`,
    `Cannot read properties of null (reading 'getClientRect')`,
    `null is not an object (evaluating 'a.ui.space("top").unselectable')`,
    'You can only create one Muuri Item per element!',
    'ckeditor',
  ],
};

export const clientIdPollIntervalMS = 30000;
